/**
 * useTrialStatus — real-time trial state for the signed-in user.
 *
 * Watches BOTH users/{uid}.trial (credits/expiry data) and
 * users/{uid}/subscription/subscription (the gate: only subscriptions written
 * by redeemAccessCode — is_trial === true or plan === 'trial' — count as
 * trials; legacy users/{uid}.trial fields on activated accounts do not).
 * Derivation lives in ./trialStatus. Fails toward "not a trial user" on any
 * error so it can never block the UI.
 */

import { useEffect, useState } from 'react';
import { resolveTabWorkspace } from '../services/prest0nRuntime/tabWorkspace';
import { Unsubscribe, doc, getFirestore, onSnapshot } from 'firebase/firestore';
import { getApp } from 'firebase/app';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import {
  NO_TRIAL,
  TrialDocShape,
  TrialStatus,
  TrialSubscriptionShape,
  WorkspaceTrialFacts,
  decideForeignWorkspaceTrialSource,
  deriveTrialStatus,
  deriveWorkspaceTrialStatus,
  parseWorkspaceEntitlementSource,
  parseWorkspaceTrialFacts,
} from './trialStatus';
import { pickBackPointerAccountId } from '../services/prest0nRuntime/backPointer';
import { usdToWu } from '../config/workUnits';

export { TRIAL_EXPIRING_SOON_DAYS, deriveTrialStatus, deriveWorkspaceTrialStatus } from './trialStatus';
export type { TrialStatus } from './trialStatus';

export function useTrialStatus(): TrialStatus {
  const [uid, setUid] = useState<string | null>(null);
  const [authResolved, setAuthResolved] = useState(false);
  const [trial, setTrial] = useState<TrialDocShape | null>(null);
  const [docLoaded, setDocLoaded] = useState(false);
  const [subscription, setSubscription] = useState<TrialSubscriptionShape | null>(null);
  const [subLoaded, setSubLoaded] = useState(false);

  useEffect(() => {
    const unsub = onAuthStateChanged(getAuth(), (fbUser) => {
      setUid(fbUser && !fbUser.isAnonymous ? fbUser.uid : null);
      setAuthResolved(true);
    });
    return unsub;
  }, []);

  useEffect(() => {
    setTrial(null);
    setDocLoaded(false);
    if (!uid) return;
    let unsub: Unsubscribe | null = null;
    try {
      const ref = doc(getFirestore(getApp()), 'users', uid);
      unsub = onSnapshot(
        ref,
        (snap) => {
          const data = snap.exists() ? (snap.data() as { trial?: TrialDocShape }) : null;
          setTrial(data?.trial ?? null);
          setDocLoaded(true);
        },
        (err) => {
          console.warn('[useTrialStatus] user-doc snapshot error', err);
          setTrial(null);
          setDocLoaded(true);
        }
      );
    } catch (err) {
      console.warn('[useTrialStatus] listener setup failed', err);
      setTrial(null);
      setDocLoaded(true);
    }
    return () => {
      if (unsub) unsub();
    };
  }, [uid]);

  useEffect(() => {
    setSubscription(null);
    setSubLoaded(false);
    if (!uid) return;
    let unsub: Unsubscribe | null = null;
    try {
      const ref = doc(getFirestore(getApp()), 'users', uid, 'subscription', 'subscription');
      unsub = onSnapshot(
        ref,
        (snap) => {
          setSubscription(snap.exists() ? (snap.data() as TrialSubscriptionShape) : null);
          setSubLoaded(true);
        },
        (err) => {
          console.warn('[useTrialStatus] subscription snapshot error', err);
          setSubscription(null);
          setSubLoaded(true);
        }
      );
    } catch (err) {
      console.warn('[useTrialStatus] subscription listener setup failed', err);
      setSubscription(null);
      setSubLoaded(true);
    }
    return () => {
      if (unsub) unsub();
    };
  }, [uid]);

  // LIVE credit tracking (2026-08-04 owner directive: the banner must match
  // the user's balance at all times). Trial spend rides the pr0 wallet —
  // users/{uid}.trial.creditRemainingCents is the redemption-time SEED and
  // never decrements — so the true remaining credit is the live wallet
  // balance × the same rate the wallet page uses. Listener attaches only for
  // trial-flagged subscriptions; the static seed stays the fallback whenever
  // wallet or rate reads fail.
  const isTrialSub = subscription?.is_trial === true || subscription?.plan === 'trial';
  const [walletOlv, setWalletOlv] = useState<number | null>(null);
  const [olvRate, setOlvRate] = useState<number | null>(null);
  useEffect(() => {
    setWalletOlv(null);
    if (!uid || !isTrialSub) return;
    let unsub: Unsubscribe | null = null;
    try {
      const ref = doc(getFirestore(getApp()), 'users', uid, 'wallet', 'summary');
      unsub = onSnapshot(
        ref,
        (snap) => {
          const raw = snap.exists() ? (snap.data() as any)?.olv_balance : null;
          setWalletOlv(typeof raw === 'number' && isFinite(raw) ? raw : null);
        },
        () => setWalletOlv(null),
      );
    } catch {
      setWalletOlv(null);
    }
    return () => {
      if (unsub) unsub();
    };
  }, [uid, isTrialSub]);
  useEffect(() => {
    if (!uid || !isTrialSub || olvRate !== null) return;
    let cancelled = false;
    (async () => {
      try {
        const { getDoc } = await import('firebase/firestore');
        const snap = await getDoc(doc(getFirestore(getApp()), 'global_olv_token', 'token_price'));
        const data = snap.exists() ? (snap.data() as any) : null;
        const raw = data?.token_price !== undefined ? data.token_price : data?.price;
        const parsed = typeof raw === 'number' ? raw : parseFloat(String(raw));
        if (!cancelled && !isNaN(parsed) && isFinite(parsed) && parsed > 0) setOlvRate(parsed);
      } catch { /* static seed remains the fallback */ }
    })();
    return () => {
      cancelled = true;
    };
  }, [uid, isTrialSub, olvRate]);

  if (!authResolved) return { ...NO_TRIAL, isLoading: true };
  if (!uid) return { ...NO_TRIAL };
  if (!docLoaded || !subLoaded) return { ...NO_TRIAL, isLoading: true };
  const status = deriveTrialStatus(trial, subscription, Date.now());
  if (status.isTrialUser && walletOlv !== null) {
    // Task #76 (2026-08-18 owner directive): ONE number everywhere. The
    // banner/subscription surfaces show the workspace's ACTUAL wallet pr0
    // (liveWalletWu) — the same figure the wallet page prints — never a
    // seed-clamped conversion that drifts from it. creditRemainingCents
    // follows the live balance too (unclamped) so exhaustion states agree.
    //
    // UNIT FIX (2026-08-21 owner report — banner "52,030 pr0" vs wallet
    // "692.1 pr0", same money): the wallet page prints usdToWu(usd value) —
    // pr0 at the standard rate — while this used to pass the RAW internal
    // OLV balance through as if it were pr0 (OLV rides its own live token
    // rate, a different number entirely). Convert exactly the way the page
    // does; without a rate there is no honest live number, so null lets the
    // banner fall back rather than print a wrong unit.
    const liveUsd = olvRate !== null ? walletOlv * olvRate : null;
    const liveCents = liveUsd !== null
      ? Math.max(0, Math.round(liveUsd * 100))
      : status.creditRemainingCents;
    return {
      ...status,
      liveWalletWu: liveUsd !== null ? Math.max(0, usdToWu(liveUsd)) : null,
      creditRemainingCents: liveCents,
    };
  }
  return status;
}

// ============================================================================
// useWorkspaceTrialStatus — trial state keyed to the ACTIVE WORKSPACE
// ============================================================================
//
// Owner report 2026-08-17: the global trial banner showed the signed-in
// MEMBER's native trial numbers ("91.74 pr0 remaining") while the wallet page
// showed the ACTING account's paying-wallet balance (137 pr0) for the same
// workspace. The banner must "reflect the wallet page the user is in at all
// times", so this hook resolves trial state the same way the wallet display
// does (WalletSummaryContext / walletDisplayResolver):
//
//   · no acting account, or acting in your own uid-keyed workspace → the
//     native useTrialStatus result, byte-for-byte;
//   · acting in a GRANTED workspace (the mirror carries entitlement_source —
//     the server's internal-grant overlay) → NO_TRIAL, whoever pays for it,
//     decided before the payer rule (2026-09-15: the internal org bills its
//     owner's personal wallet, and the payer rule handed back the owner's
//     expired personal trial — "🔒 your trial has ended" in every founders'
//     thread while the server billed the turns normally);
//   · acting in another account whose PAYING wallet is your own
//     (payer_uid === uid) → native as well: your live feeds ARE the
//     workspace's numbers;
//   · acting in a foreign workspace → the WORKSPACE's trial facts + live
//     paying-wallet balance from the member-readable display mirror
//     (accounts/{id}/wallet/displaySummary — the same doc the wallet page
//     renders). A non-trial workspace yields NO_TRIAL (banner hidden), even
//     when the member's own native account is on a trial;
//   · mirror missing / unreadable / not yet stamped with trial facts →
//     NO_TRIAL — the member's native trial must never leak into a foreign
//     workspace's banner.

type MirrorState = {
  status: 'idle' | 'loading' | 'ready' | 'missing' | 'error';
  facts: WorkspaceTrialFacts | null;
  payerUid: string | null;
  entitlementSource: string | null;
  olvBalance: number | null;
};

const MIRROR_IDLE: MirrorState = {
  status: 'idle',
  facts: null,
  payerUid: null,
  entitlementSource: null,
  olvBalance: null,
};

export function useWorkspaceTrialStatus(): TrialStatus {
  const native = useTrialStatus();

  const [uid, setUid] = useState<string | null>(null);
  const [authResolved, setAuthResolved] = useState(false);
  useEffect(() => {
    const unsub = onAuthStateChanged(getAuth(), (fbUser) => {
      setUid(fbUser && !fbUser.isAnonymous ? fbUser.uid : null);
      setAuthResolved(true);
    });
    return unsub;
  }, []);

  // Acting-account back-pointer (users/{uid}/account_membership/main) — the
  // same doc WalletSummaryContext keys the wallet display on.
  const [membershipLoaded, setMembershipLoaded] = useState(false);
  const [accountId, setAccountId] = useState<string | null>(null);
  useEffect(() => {
    setMembershipLoaded(false);
    setAccountId(null);
    if (!uid) {
      if (authResolved) setMembershipLoaded(true);
      return;
    }
    let unsub: Unsubscribe | null = null;
    try {
      const ref = doc(getFirestore(getApp()), 'users', uid, 'account_membership', 'main');
      unsub = onSnapshot(
        ref,
        (snap) => {
          // Tolerant back-pointer shapes (2026-08-19): legacy invite docs
          // carry defaultAccountId/memberships[] instead of accountId — the
          // narrow read misclassified those members as native-account users.
          const data = snap.exists() ? (snap.data() as Record<string, unknown>) : null;
          // PER-TAB ORGANIZATION (2026-09-11): the watched doc is account-wide,
          // so another device's switch moved the trial/wallet chip here while
          // the rest of the page stayed put. The tab's workspace decides.
          const pointer = pickBackPointerAccountId(data);
          setAccountId(resolveTabWorkspace({ uid, accountFromPointer: pointer }).accountId);
          setMembershipLoaded(true);
        },
        () => {
          // Fail-safe: treat as no acting account (native semantics).
          setAccountId(null);
          setMembershipLoaded(true);
        },
      );
    } catch {
      setAccountId(null);
      setMembershipLoaded(true);
    }
    return () => {
      if (unsub) unsub();
    };
  }, [uid, authResolved]);

  // Foreign-workspace display mirror (accounts/{id}/wallet/displaySummary).
  const foreignAccountId = uid && accountId && accountId !== uid ? accountId : null;
  const [mirror, setMirror] = useState<MirrorState>(MIRROR_IDLE);
  useEffect(() => {
    setMirror(foreignAccountId ? { ...MIRROR_IDLE, status: 'loading' } : MIRROR_IDLE);
    if (!foreignAccountId) return;
    let unsub: Unsubscribe | null = null;
    try {
      const ref = doc(getFirestore(getApp()), 'accounts', foreignAccountId, 'wallet', 'displaySummary');
      unsub = onSnapshot(
        ref,
        (snap) => {
          if (!snap.exists()) {
            setMirror({ ...MIRROR_IDLE, status: 'missing' });
            return;
          }
          const data = snap.data() as Record<string, unknown>;
          const rawBalance = data.olv_balance;
          setMirror({
            status: 'ready',
            facts: parseWorkspaceTrialFacts(data),
            payerUid: typeof data.payer_uid === 'string' && data.payer_uid ? data.payer_uid : null,
            entitlementSource: parseWorkspaceEntitlementSource(data),
            olvBalance: typeof rawBalance === 'number' && isFinite(rawBalance) ? rawBalance : null,
          });
        },
        () => setMirror({ ...MIRROR_IDLE, status: 'error' }),
      );
    } catch {
      setMirror({ ...MIRROR_IDLE, status: 'error' });
    }
    return () => {
      if (unsub) unsub();
    };
  }, [foreignAccountId]);

  // OLV rate for the live-credit conversion (same source + policy as the
  // native hook's rate read; fetched only when a foreign trial needs it).
  const workspaceTrialFlagged = mirror.status === 'ready' && mirror.facts !== null;
  const [olvRate, setOlvRate] = useState<number | null>(null);
  useEffect(() => {
    if (!foreignAccountId || !workspaceTrialFlagged || olvRate !== null) return;
    let cancelled = false;
    (async () => {
      try {
        const { getDoc } = await import('firebase/firestore');
        const snap = await getDoc(doc(getFirestore(getApp()), 'global_olv_token', 'token_price'));
        const data = snap.exists() ? (snap.data() as any) : null;
        const raw = data?.token_price !== undefined ? data.token_price : data?.price;
        const parsed = typeof raw === 'number' ? raw : parseFloat(String(raw));
        if (!cancelled && !isNaN(parsed) && isFinite(parsed) && parsed > 0) setOlvRate(parsed);
      } catch { /* the static seed remains the fallback */ }
    })();
    return () => {
      cancelled = true;
    };
  }, [foreignAccountId, workspaceTrialFlagged, olvRate]);

  if (!authResolved) return { ...NO_TRIAL, isLoading: true };
  if (!uid) return { ...NO_TRIAL };
  if (!membershipLoaded) return { ...NO_TRIAL, isLoading: true };
  // Own workspace (or none): the native result, byte-for-byte.
  if (!foreignAccountId) return native;
  // The order below is decided by decideForeignWorkspaceTrialSource (pure,
  // pinned): a granted workspace wins over the payer rule; the payer rule
  // (my own wallet pays this workspace → my live native feeds ARE its
  // numbers, mirroring walletDisplayResolver's payer_uid === uid rule) wins
  // over the workspace's own facts.
  const source = decideForeignWorkspaceTrialSource({
    status: mirror.status,
    payerIsMe: mirror.payerUid === uid,
    entitlementSource: mirror.entitlementSource,
  });
  if (source === 'loading') return { ...NO_TRIAL, isLoading: true };
  if (source === 'no-trial') return { ...NO_TRIAL };
  if (source === 'native') return native;
  const liveBalanceCents =
    mirror.olvBalance !== null && olvRate !== null
      ? mirror.olvBalance * olvRate * 100
      : null;
  // Same unit fix as the native hook (2026-08-21): the workspace banner's
  // live pr0 is usdToWu(usd value) — the wallet page's own conversion —
  // never the raw mirror OLV balance.
  return deriveWorkspaceTrialStatus(
    mirror.facts,
    liveBalanceCents,
    Date.now(),
    liveBalanceCents !== null ? usdToWu(liveBalanceCents / 100) : null,
  );
}

export default useTrialStatus;
